import { ChangeEvent, Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { CreateRoleDTO, useGetAuthoritiesQuery } from '../../../../../api/controllers/admin-roles';
import { InfoField } from '../../../../../components';

type ConfirmModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onCancel: () => void;
  isLoading: boolean;
  fields: CreateRoleDTO | null;
  handleEditFields: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string[]>
  ) => void;
};

export const CreateRoleModal: FC<ConfirmModalProps> = (props) => {
  const { open, onClose, onSuccess, onCancel, isLoading, fields, handleEditFields } = props;
  const { t } = useTranslation();
  const { data: authorities } = useGetAuthoritiesQuery();
  const authoritiesSelectOptions = authorities?.authorities.map((it) => {
    return (
      <MenuItem key={it} value={it}>
        {it}
      </MenuItem>
    );
  });
  // validation for required fields
  const requiredFields = ['authorities', 'name'] as (keyof CreateRoleDTO)[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined || fields[field] === '' || fields[field] === null) {
        return true;
      }
    }

    return false;
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { minWidth: '380px' } }}>
      <DialogTitle>{t('users-and-roles.roles.modal.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2} maxWidth={450} width={450}>
          <TextField
            name="name"
            value={fields?.name}
            onChange={handleEditFields}
            label={t('users-and-roles.roles.modal.fields.name')}
            fullWidth
          />

          <TextField
            name="comment"
            value={fields?.comment}
            onChange={handleEditFields}
            multiline
            label={t('users-and-roles.roles.modal.fields.comment')}
            fullWidth
          />

          <TextField
            select
            name="authorities"
            value={fields?.authorities}
            onChange={handleEditFields}
            label={t('users-and-roles.roles.modal.fields.authorities')}
            fullWidth
            SelectProps={{
              multiple: true,
              value: fields?.authorities || [],
              // @ts-ignore
              onChange: handleEditFields,
            }}
          >
            {authoritiesSelectOptions}
          </TextField>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onSuccess()}
          disabled={isLoading || checkSubmitDisabling()}
        >
          {t('users-and-roles.roles.modal.create-btn')}
        </Button>
        <Button variant="outlined" onClick={() => onCancel()} disabled={isLoading}>
          {t('users-and-roles.roles.modal.cancel-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
